import React from 'react';
import {
  ArrowDown,
  Bot,
  CheckCircle2,
  Clock,
  GitBranch,
  Grid3x3,
  Hash,
  Headphones,
  Megaphone,
  MessageSquare,
  PauseCircle,
  PhoneIncoming,
  PhoneOff,
  PhoneOutgoing,
  Repeat,
  RotateCcw,
  Server,
  Timer,
  Users,
  XCircle,
} from 'lucide-react';
import { DispositionJsonCell } from '../DispositionJsonCell';
import {
  formatSeconds,
  type AmountShare,
  type CallBilling,
  type CallFlow,
  type FlowNode,
  type FlowTone,
} from './callFlowModel';

const TONE: Record<FlowTone, { card: string; icon: string; text: string; dot: string }> = {
  blue: {
    card: 'border-sky-300 bg-sky-50 dark:border-sky-800 dark:bg-sky-950/30',
    icon: 'bg-sky-600 text-white',
    text: 'text-sky-700 dark:text-sky-300',
    dot: 'bg-sky-500',
  },
  indigo: {
    card: 'border-indigo-300 bg-indigo-50 dark:border-indigo-800 dark:bg-indigo-950/30',
    icon: 'bg-indigo-600 text-white',
    text: 'text-indigo-700 dark:text-indigo-300',
    dot: 'bg-indigo-500',
  },
  violet: {
    card: 'border-violet-300 bg-violet-50 dark:border-violet-800 dark:bg-violet-950/30',
    icon: 'bg-violet-600 text-white',
    text: 'text-violet-700 dark:text-violet-300',
    dot: 'bg-violet-500',
  },
  amber: {
    card: 'border-amber-300 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30',
    icon: 'bg-amber-500 text-white',
    text: 'text-amber-700 dark:text-amber-300',
    dot: 'bg-amber-500',
  },
  green: {
    card: 'border-emerald-300 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-950/30',
    icon: 'bg-emerald-600 text-white',
    text: 'text-emerald-700 dark:text-emerald-300',
    dot: 'bg-emerald-500',
  },
  red: {
    card: 'border-rose-300 bg-rose-50 dark:border-rose-800 dark:bg-rose-950/30',
    icon: 'bg-rose-600 text-white',
    text: 'text-rose-700 dark:text-rose-300',
    dot: 'bg-rose-500',
  },
  slate: {
    card: 'border-border bg-muted/40',
    icon: 'bg-slate-600 text-white',
    text: 'text-muted-foreground',
    dot: 'bg-slate-400',
  },
};

const nodeIcon = (node: FlowNode, isOutgoing: boolean) => {
  const p = { size: 18, strokeWidth: 2.4 };
  switch (node.kind) {
    case 'customer':
      return isOutgoing ? <PhoneOutgoing {...p} /> : <PhoneIncoming {...p} />;
    case 'did':
      return <Hash {...p} />;
    case 'ivr':
      return <GitBranch {...p} />;
    case 'queue':
      return <Users {...p} />;
    case 'ai':
      return <Bot {...p} />;
    case 'system':
      return <Megaphone {...p} />;
    case 'end':
      return node.tone === 'green' ? <CheckCircle2 {...p} /> : <PhoneOff {...p} />;
    default:
      return <Headphones {...p} />;
  }
};

const clock = (at: number) =>
  new Date(at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });

const PathStep = ({
  node,
  isOutgoing,
  base,
  next,
}: {
  node: FlowNode;
  isOutgoing: boolean;
  base: number;
  next?: FlowNode;
}) => {
  const t = TONE[node.tone];
  const events = node.events ?? [];
  const last = !next;
  return (
    <li className="flex gap-3">
      <div className="flex w-9 shrink-0 flex-col items-center">
        <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${t.icon}`}>
          {nodeIcon(node, isOutgoing)}
        </span>
        {!last ? <span className="w-0.5 flex-1 bg-border" /> : null}
      </div>
      <div className="min-w-0 flex-1 pb-4">
        <div className={`rounded-xl border-2 p-3 ${t.card}`}>
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <span className={`text-[10px] font-bold uppercase tracking-wide ${t.text}`}>{node.subtitle}</span>
            <span className="break-words text-sm font-bold text-foreground">{node.title}</span>
            {node.chips.map((c) => (
              <span
                key={c}
                className="rounded-full bg-background/80 px-2 py-0.5 text-[10px] font-medium text-muted-foreground ring-1 ring-inset ring-border"
              >
                {c}
              </span>
            ))}
          </div>
          {node.facts.length > 0 && (
            <dl className="mt-1.5 flex flex-wrap gap-x-4 gap-y-0.5 text-xs">
              {node.facts.map((f) => (
                <div key={f.label} className="flex gap-1">
                  <dt className="text-muted-foreground">{f.label}:</dt>
                  <dd className="m-0 break-words font-semibold text-foreground">{f.value}</dd>
                </div>
              ))}
            </dl>
          )}
          {events.length > 0 && (
            <ol className="m-0 mt-2 list-none space-y-1 border-t border-border/50 p-0 pt-2">
              {events.map((e) => (
                <li key={e.key} className="flex items-center gap-2 text-xs">
                  <span className={`h-2 w-2 shrink-0 rounded-full ${TONE[e.tone].dot}`} />
                  <span className="w-[58px] shrink-0 font-mono tabular-nums text-muted-foreground">{clock(e.at)}</span>
                  <span className="w-[52px] shrink-0 font-mono text-[11px] tabular-nums text-muted-foreground">
                    +{formatSeconds(Math.floor(e.at / 1000) - Math.floor(base / 1000))}
                  </span>
                  <span className={`font-semibold ${TONE[e.tone].text}`}>{e.label}</span>
                </li>
              ))}
            </ol>
          )}
        </div>
        {/* how the call moved on to the next step */}
        {next?.edgeLabel ? (
          <span
            className={`mt-2 inline-flex items-center gap-1 rounded-full bg-background px-2 py-0.5 text-[10px] font-bold ring-1 ring-inset ring-current ${
              TONE[next.edgeTone ?? 'slate'].text
            }`}
          >
            <ArrowDown size={11} strokeWidth={3} /> {next.edgeLabel}
            {next.edgeAt != null ? ` · ${clock(next.edgeAt)}` : ''}
            {next.edgeBy ? ` · by ${next.edgeBy}` : ''}
          </span>
        ) : null}
      </div>
    </li>
  );
};

const Stat = ({
  icon,
  label,
  value,
  tone = 'slate',
  children,
}: {
  icon: React.ReactNode;
  label: string;
  value?: string;
  tone?: FlowTone;
  children?: React.ReactNode;
}) => (
  <div className="flex min-w-0 items-center gap-2.5 rounded-xl border border-border/60 bg-card px-3 py-2.5">
    <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${TONE[tone].icon}`}>{icon}</span>
    <div className="min-w-0">
      <div className="text-[11px] text-muted-foreground">{label}</div>
      {value ? <div className="truncate text-sm font-bold text-foreground">{value}</div> : null}
      {children}
    </div>
  </div>
);

// One line per company.
const ShareCell = ({ list }: { list: AmountShare[] }) =>
  list.length ? (
    <div className="space-y-0.5">
      {list.map((e) => (
        <div key={e.company} className="flex justify-between gap-3 whitespace-nowrap">
          <span className="text-muted-foreground">{e.company}:</span>
          <span className="font-semibold tabular-nums text-foreground">{e.value}</span>
        </div>
      ))}
    </div>
  ) : (
    <span className="text-muted-foreground">—</span>
  );

// Stored "YYYY-MM-DD HH:mm:ss" → time part; the date is the call's date for every leg.
const timeOnly = (v: string) => (v ? v.replace('T', ' ').slice(11, 19) || v : '—');

const secs = (v: number | null) => (v ? formatSeconds(v) : '0s');
// Raw stored seconds, no minute conversion.
const rawSecs = (v: number | null) => `${v || 0}s`;

const InfoPill = ({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) => (
  <span className="inline-flex items-center gap-1.5 rounded-full border border-border/60 bg-card px-2.5 py-1 text-xs">
    <span className="text-muted-foreground">{icon}</span>
    <span className="text-muted-foreground">{label}:</span>
    <span className="font-semibold text-foreground">{value}</span>
  </span>
);

/** Call Legs tab: per-leg ids, times, durations, pulses and credits. */
/** hideBilling: no pulses / credits columns (call-centre admin, team lead, agent). */
export const CallLegsTable = ({ billing, hideBilling }: { billing: CallBilling; hideBilling?: boolean }) => (
  <section>
    <div className="space-y-3 rounded-2xl border border-border/60 bg-card p-4">
      <div className="flex flex-wrap gap-2">
        {billing.pbxName ? <InfoPill icon={<Server size={13} />} label="PBX" value={billing.pbxName} /> : null}
        {billing.declinedBy ? <InfoPill icon={<PhoneOff size={13} />} label="Call Disconnected by" value={billing.declinedBy} /> : null}
        {billing.isRetryKnown ? (
          <InfoPill
            icon={<RotateCcw size={13} />}
            label="Retry call"
            value={
              billing.retry
                ? ['Yes', billing.retry.attempt && `attempt #${billing.retry.attempt}`, billing.retry.retryCallId]
                    .filter(Boolean)
                    .join(' · ')
                : 'No'
            }
          />
        ) : null}
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[1900px] text-left text-sm">
          <thead className="text-muted-foreground">
            <tr className="border-b border-border/60">
              {[
                'Leg', 'Call SID', 'SIP Call ID', 'Leg Type', 'Status', 'Start', 'End', 'Ring', 'Talk Time', 'Hold', 'Mute',
                'Hangup Reason', 'Failure Reason', /* 'Declined by', */ 'Disposition', ...(hideBilling ? [] : ['Pulses', 'Credits Deducted']),
              ].map((h) => (
                <th key={h} className="whitespace-nowrap px-2 py-1.5 font-semibold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {billing.legs.map((l) => (
              <tr key={l.key} className="border-b border-border/40 align-top last:border-0">
                <td className="px-2 py-1.5">
                  <div className="font-semibold text-foreground">{l.who}</div>
                  <div className="text-xs text-muted-foreground">{l.role}</div>
                </td>
                <td className="whitespace-nowrap px-2 py-1.5 font-mono text-[13px]">{l.callSid || '—'}</td>
                <td className="max-w-[240px] break-all px-2 py-1.5 font-mono text-[13px]">{l.sipCallId || '—'}</td>
                <td className="whitespace-nowrap px-2 py-1.5">{l.legType || '—'}</td>
                <td className="px-2 py-1.5">
                  <span
                    className={`whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-semibold ${
                      l.answered
                        ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300'
                        : 'bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300'
                    }`}
                  >
                    {l.answered ? 'Answered' : 'Not answered'}
                  </span>
                </td>
                <td className="whitespace-nowrap px-2 py-1.5 tabular-nums" title={l.startTime}>{timeOnly(l.startTime)}</td>
                <td className="whitespace-nowrap px-2 py-1.5 tabular-nums" title={l.endTime}>{timeOnly(l.endTime)}</td>
                <td className="px-2 py-1.5 tabular-nums">{rawSecs(l.ringSec)}</td>
                <td className="px-2 py-1.5 tabular-nums">{rawSecs(l.talkSec)}</td>
                <td className="px-2 py-1.5 tabular-nums">{rawSecs(l.holdSec)}</td>
                <td className="px-2 py-1.5 tabular-nums">{rawSecs(l.muteSec)}</td>
                <td className="px-2 py-1.5">{l.hangupReason || '—'}</td>
                <td className={`px-2 py-1.5 ${l.failureReason ? 'font-medium text-rose-600 dark:text-rose-400' : ''}`}>{l.failureReason || '—'}</td>
                {/* <td className="whitespace-nowrap px-2 py-1.5">{l.declinedBy || '—'}</td> */}
                <td className="min-w-[220px] px-2 py-1.5"><DispositionJsonCell value={l.disposition} /></td>
                {hideBilling ? null : <td className="px-2 py-1.5"><ShareCell list={l.pulses} /></td>}
                {hideBilling ? null : <td className="px-2 py-1.5"><ShareCell list={l.credits} /></td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  </section>
);

export interface CallFlowViewProps {
  flow: CallFlow;
  isOutgoing: boolean;
  /** Extra highlight chips (recording, AI notes, ...) rendered after the built-in ones. */
  extraHighlights?: React.ReactNode;
  /** Hide credits deducted (call centre admin). */
  hideCredits?: boolean;
  /** Opens the IVR Journey tab; the button shows only when given. */
  onViewIvrJourney?: () => void;
  children?: React.ReactNode;
}

export default function CallFlowView({ flow, isOutgoing, extraHighlights, onViewIvrJourney, children }: CallFlowViewProps) {
  const customerBill = flow.billing.legs.find((l) => l.role === 'Customer');
  const customerConnected = Boolean(customerBill?.answered); // stored call_status, billing fallback
  // Green when the call reached whoever was meant to handle it: an agent, or the IVR itself.
  const success = flow.mode === 'agent' ? flow.answered : customerConnected;
  const ended = [flow.billing.declinedBy && `Ended by ${flow.billing.declinedBy}`, flow.endReason]
    .filter(Boolean)
    .join(' · ');

  const headline =
    flow.mode === 'ivr'
      ? `Handled in IVR${flow.ivrName ? ` · ${flow.ivrName}` : ''}${customerConnected ? '' : ' — not answered'}`
      : flow.mode === 'none'
      ? customerConnected
        ? 'Call ended before routing'
        : 'Call not connected'
      : flow.answered
      ? `Answered by ${flow.answeredBy}${flow.transfers ? `, transferred ${flow.transfers}×` : ''}${flow.conferences ? `, conference ${flow.conferences}×` : ''}`
      : isOutgoing
      ? 'Customer did not answer'
      : flow.missedBy.length
      ? 'Missed — no agent answered'
      : 'Not connected to an agent';

  return (
    <div className="space-y-5">
      {/* One-line verdict + key numbers */}
      <div
        className={`flex flex-wrap items-center gap-2 rounded-xl border-2 px-4 py-3 ${
          success ? TONE.green.card : TONE.red.card
        }`}
      >
        {success ? (
          <CheckCircle2 size={20} className={TONE.green.text} />
        ) : (
          <XCircle size={20} className={TONE.red.text} />
        )}
        <span className="text-base font-bold text-foreground">{headline}</span>
        {ended ? <span className="text-xs text-muted-foreground">{ended}</span> : null}
        {flow.failureReason ? (
          <span className="rounded-full bg-rose-100 px-2.5 py-0.5 text-xs font-semibold text-rose-800 dark:bg-rose-950/50 dark:text-rose-300">
            Failure: {flow.failureReason}
          </span>
        ) : null}
        {flow.missedBy.length > 0 && (
          <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-800 dark:bg-amber-950/50 dark:text-amber-300">
            Missed by {flow.missedBy.join(', ')}
          </span>
        )}
        {extraHighlights}
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {/* <Stat
          icon={<UserCheck size={16} />}
          label="Answered by"
          value={
            flow.answered
              ? flow.answeredBy || '—'
              : flow.mode === 'ivr'
                ? flow.ivrName || 'IVR'
                : '—'
          }
          tone="green"
        /> */}
        {flow.mode === 'ivr' ? (
          <>
            {/* billing_duration of the customer leg, as stored. */}
            <Stat icon={<MessageSquare size={16} />} label="IVR time" value={secs(customerBill?.billingSec ?? 0)} tone="violet" />
            <Stat icon={<Grid3x3 size={16} />} label="Keys pressed" value={flow.dtmf.length ? flow.dtmf.join(' → ') : 'None'} tone="indigo">
              {onViewIvrJourney ? (
                <button
                  type="button"
                  onClick={onViewIvrJourney}
                  className="mt-0.5 cursor-pointer border-0 bg-transparent p-0 text-[11px] font-semibold text-primary hover:underline"
                >
                  View IVR journey →
                </button>
              ) : null}
            </Stat>
          </>
        ) : null}
        {flow.mode === 'agent' ? (
          <>
            {/* Hold is recorded on agent legs: time the agent kept the customer on hold. */}
            <Stat
              icon={<PauseCircle size={16} />}
              label="Customer ring & hold"
              value={`Ring ${secs(flow.custRingSec)} · Hold ${secs(flow.holdSec)}`}
              tone="violet"
            />
            {flow.transfers > 0 ? (
              <Stat icon={<Repeat size={16} />} label="Transfers" value={String(flow.transfers)} tone="indigo" />
            ) : null}
            {!isOutgoing && flow.agentsTried > 0 ? (
              <Stat icon={<Users size={16} />} label="Agents tried" value={String(flow.agentsTried)} tone="amber">
                {flow.agentsTriedList.map((a) => (
                  <div key={a.key} className="mt-1 break-words text-xs leading-tight text-foreground">
                    <div className="font-semibold">{a.name}</div>
                    <div className="text-muted-foreground">
                      {[a.number !== a.name ? a.number : '', a.id ? `ID ${a.id}` : ''].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                ))}
              </Stat>
            ) : null}
          </>
        ) : null}
        {/* Call duration = customer leg total_duration; talk time per leg = stored patch_duration. */}
        <Stat icon={<Timer size={16} />} label="Total duration" value={secs(flow.totalSec)} tone="blue">
          {flow.legPatches.map((p) => (
            <div key={p.key} className="truncate text-xs text-foreground" title={p.who}>
              <span className="text-muted-foreground">{p.who} talk time: </span>
              <span className="font-semibold">{secs(p.sec)}</span>
            </div>
          ))}
        </Stat>
      </div>

      {/* Call path: each step with its own events, top to bottom */}
      <section>
        <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          <Clock size={13} /> Call Path
        </div>
        <div className="rounded-2xl border border-border/60 bg-card p-4">
          <ol className="m-0 list-none p-0">
            {flow.nodes.map((node, i) => (
              <PathStep
                key={node.key}
                node={node}
                isOutgoing={isOutgoing}
                base={flow.timeline[0]?.at ?? flow.startAt ?? 0}
                next={flow.nodes[i + 1]}
              />
            ))}
          </ol>
        </div>
      </section>

      {children}
    </div>
  );
}
